import { Component, OnInit } from '@angular/core';
import { NbDialogRef, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import {
  DozentService,
  TestExerciseRecipient,
} from '../../../services/dozent.service';

interface OwnExercise {
  id: string;
  title: string;
  exercise_type: string;
  description?: string;
}

interface OwnDozentBot {
  id: string;
  name: string;
  visual_name?: string;
}

@Component({
  selector: 'app-test-exercise-dispatch-dialog',
  templateUrl: './test-exercise-dispatch-dialog.component.html',
  styleUrl: './test-exercise-dispatch-dialog.component.scss',
})
export class TestExerciseDispatchDialogComponent implements OnInit {
  exercises: OwnExercise[] = [];
  bots: OwnDozentBot[] = [];
  recipient: TestExerciseRecipient | null = null;

  loading = true;
  saving = false;

  selectedExerciseId: string | null = null;
  selectedBotId: string | null = null;
  instructions = '';
  dueDate: string | null = null;
  scheduledPublishAt: string | null = null;

  constructor(
    protected dialogRef: NbDialogRef<TestExerciseDispatchDialogComponent>,
    private dozentService: DozentService,
    private toastrService: NbToastrService,
    private t: TranslateService
  ) {}

  ngOnInit(): void {
    this.loading = true;
    Promise.all([
      this.dozentService.listOwnExercises().toPromise(),
      this.dozentService.listOwnDozentBots().toPromise(),
      this.dozentService.getTestExerciseRecipient().toPromise(),
    ])
      .then(([exercises, bots, recipient]: any) => {
        this.exercises = exercises ?? [];
        this.bots = bots ?? [];
        this.recipient = recipient ?? null;
        if (this.bots.length === 1) {
          this.selectedBotId = this.bots[0].id;
        }
        this.loading = false;
      })
      .catch(() => {
        this.loading = false;
        this.toastrService.danger(this.t.instant('toasts.error'), '');
      });
  }

  recipientLabel(): string {
    if (!this.recipient) {
      return '';
    }
    const name = (this.recipient.name || '').trim();
    if (name) {
      return name;
    }
    const parts = [this.recipient.first_name, this.recipient.last_name]
      .filter(Boolean)
      .join(' ')
      .trim();
    return parts || this.recipient.email || this.recipient.id;
  }

  botDisplayName(bot: OwnDozentBot): string {
    return (bot.visual_name || bot.name || '').trim();
  }

  botExternalName(bot: OwnDozentBot): string | null {
    const externalName = (bot.visual_name || '').trim();
    return externalName || null;
  }

  botInternalName(bot: OwnDozentBot): string | null {
    const internalName = (bot.name || '').trim();
    return internalName || null;
  }

  canSubmit(): boolean {
    return (
      !this.saving &&
      !!this.selectedExerciseId &&
      !!this.selectedBotId &&
      !!this.recipient?.id
    );
  }

  submit(): void {
    if (!this.canSubmit()) return;
    this.saving = true;
    this.dozentService
      .dispatchTestExercise({
        exercise_id: this.selectedExerciseId!,
        bot_id: this.selectedBotId!,
        instructions: this.instructions || undefined,
        due_date: this.dueDate || undefined,
        scheduled_publish_at: this.localDateTimeToIso(this.scheduledPublishAt),
      })
      .subscribe({
        next: () => {
          this.toastrService.success(
            this.t.instant('pages.dozent.exercises.testDispatched'),
            ''
          );
          this.dialogRef.close();
        },
        error: () => {
          this.saving = false;
          this.toastrService.danger(this.t.instant('toasts.error'), '');
        },
      });
  }

  close(): void {
    this.dialogRef.close();
  }

  private localDateTimeToIso(value: string | null): string | undefined {
    if (!value) return undefined;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return undefined;
    return date.toISOString();
  }
}
